import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Check, X, Users, UserPlus, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { invitationsApi } from '../services/invitationsApi';
import { btn } from '../../utils/buttonStyles';
import toast from 'react-hot-toast';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffDays = Math.floor((Date.now() - date) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${Math.floor(diffMonths / 12)}y ago`;
};

const typeLabel = { group: 'Squad / Group', referral: 'App Referral', managed_player: 'Coach Invite' };
const typeIcon = { group: Users, referral: UserPlus, managed_player: UserPlus };

export default function InvitationsPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await invitationsApi.getMyInvitations();
      setInvitations(res?.data || res || []);
    } catch {
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (inv) => {
    setActingOn(inv.id);
    try {
      await invitationsApi.acceptInvitation(inv.id);
      toast.success(inv.type === 'group' ? `Joined ${inv.groupName || 'the group'}!` : 'Invitation accepted');
      setInvitations(prev => prev.filter(i => i.id !== inv.id));
      if (inv.type === 'group' && inv.useBy) {
        const groupId = inv.useBy.replace('group:', '');
        navigate(`/squads/${groupId}`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to accept invitation');
    } finally {
      setActingOn(null);
    }
  };

  const handleDecline = async (inv) => {
    setActingOn(inv.id);
    try {
      await invitationsApi.declineInvitation(inv.id);
      toast.success('Invitation declined');
      setInvitations(prev => prev.filter(i => i.id !== inv.id));
    } catch (err) {
      toast.error(err.message || 'Failed to decline invitation');
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className={`text-2xl font-bold ${theme.text} mb-6`}>My Invitations</h1>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className={`w-8 h-8 animate-spin ${theme.textMuted}`} />
        </div>
      ) : invitations.length === 0 ? (
        <div className={`${theme.card} rounded-lg ${theme.shadow} p-12 text-center`}>
          <Mail className={`w-12 h-12 mx-auto mb-4 ${theme.textMuted}`} />
          <p className={`${theme.textMuted} text-lg`}>No pending invitations</p>
        </div>
      ) : (
        <div className="space-y-4">
          {invitations.map((inv) => {
            const Icon = typeIcon[inv.type] || Mail;
            const isActing = actingOn === inv.id;
            return (
              <div key={inv.id} className={`${theme.card} rounded-lg ${theme.shadow} p-4 flex items-start gap-4`}>
                <div className={`w-10 h-10 rounded-full ${theme.gradient} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${theme.text}`}>
                    {inv.type === 'group'
                      ? <><span className="font-semibold">{inv.inviterName}</span> invited you to join <span className="font-semibold">{inv.groupName || 'a group'}</span></>
                      : inv.type === 'referral'
                      ? <><span className="font-semibold">{inv.inviterName}</span> invited you to join ExSports</>
                      : <><span className="font-semibold">{inv.inviterName}</span> sent you an invitation</>
                    }
                  </p>
                  <p className={`text-sm ${theme.textMuted} mt-0.5`}>
                    {typeLabel[inv.type] || 'Invitation'} · {formatRelativeTime(inv.createdAt)}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleAccept(inv)}
                    disabled={isActing}
                    className={`flex items-center gap-1.5 ${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
                  >
                    {isActing ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Accept
                  </button>
                  <button
                    onClick={() => handleDecline(inv)}
                    disabled={isActing}
                    className={`flex items-center gap-1.5 ${btn.base} ${btn.sm} ${theme.buttonGhost}`}
                  >
                    <X className="w-4 h-4" />
                    Decline
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
